// @flow
import styled from "styled-components";
import {
  mediumHeading,
  regularHeading,
  PhotoCarousel,
  media,
} from "@nested/brand";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBed, faHome, faFile } from "@fortawesome/free-solid-svg-icons";
import { PropertyStatus } from "@nested/brand/src/slices/PropertyCarousel/PropertyStatus";
import {
  getImage,
  formatPrice,
  PropertyType,
  type PropertyTypeEnum,
} from "@nested/utils";

const PriceText = styled.h3`
  ${regularHeading}
  margin: 0;
`;

const PriceQualifierText = styled.p`
  font-size: 12px;
  margin: 0 0 5px;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 21px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.hague70};
`;

const bedroomsText = (numBedrooms) => {
  if (numBedrooms === 1) {
    return `${numBedrooms} bed`;
  }

  return `${numBedrooms} beds`;
};

const BackgroundWrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.sand80};
  box-sizing: border-box;
  margin: 0;
  width: 100%;
  padding: 0 0 40px;
  ${media.desktop`
    padding: 80px 0;
  `}
`;

const TopSection = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;

  ${media.desktop`
    padding: 0px 20px;
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
  `};
`;

const PropertyInfoSection = styled.div`
  padding: 0 20px;
  ${media.desktop`
    width: 50%;
    margin: 0 auto 0 0;
    padding: 0;

    hr {
      margin-bottom: 20px;
    }
  `};
`;

const AddressTitle = styled.h2`
  ${regularHeading}
  text-align: left;
  margin: 15px 0 10px;
  vertical-align: middle;

  ${media.tablet`
    ${mediumHeading}
    margin-top: 20px;
  `};
`;

const PriceStatusContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
`;

const PropertyDetailContainer = styled.div`
  margin: 10px 0 0;
  display: flex;
  align-items: center;
`;

const PropertyDetail = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.5;
  letter-spacing: 2px;
  color: ${({ theme }) => theme.palette.hague};
  margin-bottom: 0px;
  margin-top: 0px;
  margin-right: 20px;
  text-transform: uppercase;

  svg {
    margin-right: 5px;
  }
`;

const Hr = styled.hr`
  margin-bottom: 10px;
  margin-top: 0px;
  border: none;
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.hague40};
`;

type PropertyDetailIconProps = {
  bedrooms?: number,
  propertyType: PropertyTypeEnum,
  className?: string,
  tenure?: ?string,
};

export const PropertyDetailIcons = ({
  bedrooms,
  propertyType,
  className,
  tenure,
}: PropertyDetailIconProps) => (
  <PropertyDetailContainer className={className}>
    <PropertyDetail>
      <FontAwesomeIcon icon={faHome} css="height: 16px;padding-bottom: 3px;" />
      <PropertyType propertyType={propertyType} />
    </PropertyDetail>
    {bedrooms && (
      <PropertyDetail>
        <FontAwesomeIcon icon={faBed} css="height: 16px;padding-bottom: 3px;" />
        {bedroomsText(bedrooms)}
      </PropertyDetail>
    )}
    {tenure && (
      <PropertyDetail>
        <FontAwesomeIcon
          icon={faFile}
          css="height: 16px;padding-bottom: 3px;"
        />
        {tenure}
      </PropertyDetail>
    )}
  </PropertyDetailContainer>
);

type Props = {
  bedrooms?: number,
  externalId: string,
  formattedAddress: string,
  images: string[],
  price: number,
  priceQualifier: string,
  propertyType: PropertyTypeEnum,
  status: string,
  tenure?: ?string,
};

export const HeroSection = ({
  bedrooms,
  externalId,
  formattedAddress,
  images,
  price,
  priceQualifier,
  propertyType,
  status,
  tenure,
}: Props) => (
  <BackgroundWrapper>
    <TopSection>
      <PhotoCarousel
        photos={images.map((imagePath) =>
          getImage(`property-listings/${externalId}/${imagePath}`),
        )}
      />

      <PropertyInfoSection limit>
        <AddressTitle>{`${formattedAddress}`}</AddressTitle>

        <Hr />

        <div css="margin: 26px 0;">
          {priceQualifier && (
            <PriceQualifierText>{priceQualifier}</PriceQualifierText>
          )}
          <PriceStatusContainer>
            <PriceText>{formatPrice(price)}</PriceText>
            <PropertyStatus status={status} />
          </PriceStatusContainer>
        </div>

        <PropertyDetailIcons
          bedrooms={bedrooms}
          propertyType={propertyType}
          tenure={tenure}
        />
      </PropertyInfoSection>
    </TopSection>
  </BackgroundWrapper>
);
